import { useEffect, useRef, useState } from "react";
import { Bot, Send, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = () => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", text }, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, language, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.delta) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                next[next.length - 1] = { ...last, text: last.text + data.delta };
                return next;
              });
            }
            if (data.session_id) setSessionId(data.session_id);
            if (data.error) throw new Error("stream error");
          } catch (parseErr) {
            if (parseErr.message === "stream error") throw parseErr;
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", text: t.contact.chatError };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t.contact.chatOpen}
        className="fixed bottom-44 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#1d6fd1] text-white shadow-lg transition-transform duration-200 hover:scale-110"
        data-testid="chat-open-button"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-7 w-7" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[250px] left-4 right-4 z-50 flex flex-col border border-[#e2e8f0] bg-white shadow-2xl sm:left-auto sm:right-6 sm:w-[360px]"
          data-testid="chat-panel"
        >
          <div className="flex items-center gap-2 bg-[#0d2b52] px-4 py-3">
            <Bot className="h-5 w-5 text-[#2f9be8]" />
            <span className="font-display text-sm font-bold text-white">{t.contact.chatTitle}</span>
          </div>

          <div className="h-80 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            <div className="max-w-[85%] bg-[#f1f5f9] px-3 py-2 text-sm leading-relaxed text-[#0d2b52]" data-testid="chat-welcome">
              {t.contact.chatWelcome}
            </div>
            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === "user"
                    ? "ml-auto max-w-[85%] bg-[#1d6fd1] px-3 py-2 text-sm text-white"
                    : "max-w-[85%] bg-[#f1f5f9] px-3 py-2 text-sm leading-relaxed text-[#0d2b52]"
                }
                data-testid={`chat-message-${i}`}
              >
                {m.text}
                {m.role === "assistant" && sending && i === messages.length - 1 && !m.text && (
                  <span className="inline-block animate-pulse">…</span>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={sendMessage} className="flex gap-2 border-t border-[#e2e8f0] p-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.contact.chatPlaceholder}
              className="rounded-none border-[#cbd5e1] text-sm focus-visible:ring-[#1d6fd1]"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={sending}
              aria-label={t.contact.chatSend}
              className="shrink-0 rounded-none bg-[#1d6fd1] hover:bg-[#0d2b52] disabled:opacity-60"
              data-testid="chat-send-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
